import { useCallback, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ListResponse, PostDTO, PostListQuery } from '@bb/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { useLiveEvent } from '@/lib/live';

type Pages = { pages: ListResponse<PostDTO>[]; pageParams: unknown[] };

/**
 * Not every cache under the `posts` key is an infinite query — the emergency
 * banner keeps a plain list there too. Reaching for `.pages` unconditionally
 * threw on those, which is what broke the feed on the first pin or ack.
 */
function mapCachedPosts(
  queryClient: QueryClient,
  transform: (items: PostDTO[]) => PostDTO[],
) {
  queryClient.setQueriesData({ queryKey: ['posts'] }, (data: unknown) => {
    if (!data || typeof data !== 'object') return data;

    if (Array.isArray((data as Pages).pages)) {
      const paged = data as Pages;
      return {
        ...paged,
        pages: paged.pages.map((page) => ({ ...page, items: transform(page.items) })),
      };
    }

    if (Array.isArray((data as ListResponse<PostDTO>).items)) {
      const flat = data as ListResponse<PostDTO>;
      return { ...flat, items: transform(flat.items) };
    }

    return data;
  });
}

function patchCaches(queryClient: QueryClient, post: PostDTO) {
  mapCachedPosts(queryClient, (items) =>
    items.map((item) => (item.id === post.id ? post : item)),
  );
}

function dropFromCaches(queryClient: QueryClient, id: string) {
  mapCachedPosts(queryClient, (items) => items.filter((item) => item.id !== id));
}

export type FeedFilters = {
  targetIds?: string[] | undefined;
  isRead?: boolean | undefined;
  isPinned?: boolean | undefined;
};

function matchesFilters(post: PostDTO, filters: FeedFilters): boolean {
  if (filters.targetIds?.length && !filters.targetIds.includes(post.author.targetId)) return false;
  if (filters.isRead !== undefined && post.isRead !== filters.isRead) return false;
  if (filters.isPinned !== undefined && post.isPinned !== filters.isPinned) return false;
  return true;
}

/**
 * Paginated history plus a live head.
 *
 * Arrivals are kept in their own array rather than spliced into the query cache
 * so that cursor pagination stays consistent: the keyset cursor refers to the
 * server's ordering, and inserting rows above it must not shift the seek point.
 */
export function usePostStream(
  scope: string,
  params: PostListQuery,
  filters: FeedFilters,
  options: { liveArrivals?: boolean } = {},
) {
  const { liveArrivals = true } = options;
  const queryClient = useQueryClient();
  const [livePosts, setLivePosts] = useState<PostDTO[]>([]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const query = useInfiniteQuery({
    queryKey: queryKeys.posts(scope, params),
    queryFn: ({ pageParam, signal }) =>
      api.posts.list(pageParam ? { ...params, cursor: pageParam } : params, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.page.nextCursor ?? undefined,
  });

  // Search results are a snapshot of a query, not a live window: a new post
  // must not appear at the top just because it arrived, when the caller asked
  // for "everything matching X between two dates".
  useLiveEvent('post.created', (post) => {
    if (!liveArrivals) return;
    if (!matchesFilters(post, filtersRef.current)) return;
    setLivePosts((current) => (current.some((p) => p.id === post.id) ? current : [post, ...current]));
  });

  useLiveEvent('post.updated', (post) => {
    setLivePosts((current) => current.map((item) => (item.id === post.id ? post : item)));
    patchCaches(queryClient, post);
  });

  useLiveEvent('post.deleted', ({ id }) => {
    setLivePosts((current) => current.filter((item) => item.id !== id));
    dropFromCaches(queryClient, id);
  });

  const items = useMemo(() => {
    const seen = new Set<string>();
    const merged: PostDTO[] = [];

    for (const post of livePosts) {
      if (seen.has(post.id)) continue;
      seen.add(post.id);
      merged.push(post);
    }
    for (const page of query.data?.pages ?? []) {
      for (const post of page.items) {
        if (seen.has(post.id)) continue;
        seen.add(post.id);
        merged.push(post);
      }
    }
    return merged;
  }, [livePosts, query.data]);

  const liveIds = useMemo(() => new Set(livePosts.map((post) => post.id)), [livePosts]);

  const patchLocal = useCallback(
    (post: PostDTO) => {
      setLivePosts((current) => current.map((item) => (item.id === post.id ? post : item)));
      patchCaches(queryClient, post);
    },
    [queryClient],
  );

  return {
    items,
    liveIds,
    query,
    patchLocal,
    total: query.data?.pages[0]?.total ?? null,
  };
}

export function usePostActions(patchLocal: (post: PostDTO) => void) {
  const queryClient = useQueryClient();

  const update = useMutation({
    mutationFn: ({ post, patch }: { post: PostDTO; patch: Partial<PostDTO> & object }) =>
      api.posts.update(post.id, {
        ...(patch.isRead !== undefined ? { isRead: patch.isRead } : {}),
        ...(patch.isPinned !== undefined ? { isPinned: patch.isPinned } : {}),
        ...(patch.note !== undefined ? { note: patch.note } : {}),
        ...(patch.tags !== undefined ? { tagIds: patch.tags.map((tag) => tag.id) } : {}),
      }),
    onMutate: ({ post, patch }) => {
      // Optimistic: the server echoes the change back over SSE, but a pin
      // should feel instant.
      const previous = post;
      patchLocal({ ...post, ...patch });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) patchLocal(context.previous);
    },
    onSuccess: ({ post }) => {
      patchLocal(post);
      void queryClient.invalidateQueries({ queryKey: queryKeys.status });
    },
  });

  return {
    togglePin: (post: PostDTO) => update.mutate({ post, patch: { isPinned: !post.isPinned } }),
    toggleRead: (post: PostDTO) => update.mutate({ post, patch: { isRead: !post.isRead } }),
    setTags: (post: PostDTO, tags: PostDTO['tags']) => update.mutate({ post, patch: { tags } }),
    isPending: update.isPending,
  };
}
